import {Link} from '@github-ui/react-router'
import type {SidebarLink} from '@github-ui/saved-views'
import {useAnalytics} from '@github-ui/use-analytics'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, NavList, Truncate} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'
import {getHandlerNavItemClick} from '../../utils/sidebar-link'
import styles from './SidebarList.module.css'
import {SidebarLinkIcon} from '../sidebar-link-icon/SidebarLinkIcon'
import {dashboardTypeLabels, type DashboardTypes} from '../../types/dashboard-types'

interface SidebarListProps {
  dashboardType: DashboardTypes
  links: SidebarLink[]
  onSelectLink: (linkItem: SidebarLink) => void
  selectedLink: string
  generateLinkPath: (path: string | undefined, query?: string) => string
  forceHardNavigation?: boolean
}

function getLinkTo(linkItem: SidebarLink, generateLinkPath: SidebarListProps['generateLinkPath']): string {
  if (linkItem.path !== undefined) {
    return generateLinkPath(linkItem.path)
  }
  return generateLinkPath(linkItem.id, linkItem.query || undefined)
}

export function SidebarList({
  dashboardType,
  links,
  selectedLink,
  onSelectLink,
  generateLinkPath,
  forceHardNavigation = false,
}: SidebarListProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  return (
    <NavList>
      {links.map(linkItem => {
        const path = getLinkTo(linkItem, generateLinkPath)
        const handleClick = getHandlerNavItemClick({dashboardType, onSelectLink, sendAnalyticsEvent, linkItem})
        const isCurrent = selectedLink === linkItem.id && 'page'

        if (forceHardNavigation) {
          return (
            <NavList.Item aria-current={isCurrent} key={linkItem.id} href={path} onClick={handleClick}>
              <NavList.LeadingVisual>
                <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
              </NavList.LeadingVisual>
              <Truncate title={linkItem.name} maxWidth="100%">
                {linkItem.name}
              </Truncate>
            </NavList.Item>
          )
        }

        return (
          <NavList.Item as={Link} aria-current={isCurrent} key={linkItem.id} to={path} onClick={handleClick}>
            <NavList.LeadingVisual>
              <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
            </NavList.LeadingVisual>
            <Truncate title={linkItem.name} maxWidth="100%">
              {linkItem.name}
            </Truncate>
          </NavList.Item>
        )
      })}
    </NavList>
  )
}

interface SidebarListCollapsedProps extends SidebarListProps {
  onCreateView?: () => void
  showCreateSavedViews?: boolean
}

export function SidebarListCollapsed({
  onSelectLink,
  onCreateView,
  showCreateSavedViews = false,
  generateLinkPath,
  ...props
}: SidebarListCollapsedProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedLinkItem = props.links.find(link => link.id === props.selectedLink)
  const listName = selectedLinkItem?.name || 'Search'

  return (
    <>
      <Button
        ref={buttonRef}
        variant="invisible"
        onClick={() => setIsDialogOpen(true)}
        trailingVisual={TriangleDownIcon}
        aria-describedby="types-collapsed-button-description"
        className={styles.sidebarToggleButton}
      >
        <h1 className={styles.sidebarHeading}>
          <Truncate title={listName} maxWidth="100%">
            {listName}
          </Truncate>
        </h1>
      </Button>
      <span className="sr-only" id="types-collapsed-button-description" aria-live="polite">
        Change the {dashboardTypeLabels[props.dashboardType]} type filter
      </span>
      {isDialogOpen && (
        <Dialog
          className={styles.dialog}
          onClose={() => setIsDialogOpen(false)}
          title="Views"
          width="small"
          returnFocusRef={buttonRef}
          footerButtons={
            showCreateSavedViews && onCreateView
              ? [
                  {buttonType: 'default', content: 'Close', onClick: () => setIsDialogOpen(false)},
                  {
                    buttonType: 'primary',
                    content: 'New view',
                    onClick: onCreateView,
                  },
                ]
              : []
          }
        >
          <SidebarList
            onSelectLink={linkItem => {
              onSelectLink(linkItem)
              setIsDialogOpen(false)
            }}
            generateLinkPath={generateLinkPath}
            {...props}
          />
        </Dialog>
      )}
    </>
  )
}
